import { createTheme } from '@mui/material/styles';
import { buildPalette } from './tokens';

/**
 * Outlined-field label size. Must drive both the label and every notched outline's
 * <legend> (MuiOutlinedInput and the date picker's), or the border cuts through the label.
 */
const FIELD_LABEL_SIZE = '1.3rem';

/**
 * Content-box height of a field's editable area, for both `MuiInputBase.input` and the
 * date picker's `sectionsContainer`. They must match or rows misalign.
 */
const FIELD_CONTENT_HEIGHT = '1.75rem';

/**
 * Build the app theme. Theme ids are parameters so the picker can rebuild live;
 * everything else is scheme-driven by MUI's CSS variables.
 * @param {{light: string, dark: string}} [themes] - "<family>.<tier>" per scheme
 */
export function createAppTheme(themes = {}) {
  return createTheme({
    // MUI stamps `data-light`/`data-dark` on <html>; never set it by hand.
    cssVariables: {
      colorSchemeSelector: 'data',
    },
    // Placeholders take the `placeholder` token at full strength. MUI's default dims the
    // text color to 42–50%, which reads under 3:1 on the field well.
    colorSchemes: {
      light: { palette: buildPalette('light', themes.light), opacity: { inputPlaceholder: 1 } },
      dark: { palette: buildPalette('dark', themes.dark), opacity: { inputPlaceholder: 1 } },
    },
    typography: {
      h2: {
        fontSize: 32,
        fontWeight: 500,
      },
      h5: {
        fontWeight: 'bold',
      },
      fontWeightRegular: 500,
    },

    // MUI's defaults.
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
    shape: {
      borderRadius: 4, // 0.25rem
    },
    custom: {
      mainNavHeight: '96px',
      maxHeight: 'calc(100vh - 96px)',
      sideNavWidth: '250px',
      contentMargin: '10px',
    },
    components: {
      MuiInputBase: {
        styleOverrides: {
          input: ({ theme: t }) => ({
            height: FIELD_CONTENT_HEIGHT,
            '&::placeholder': { color: (t.vars || t).palette.placeholder },
          }),
        },
      },
      // MUI X date pickers render their own parallel tree (MuiPickersInputBase), so every
      // text-input rule needs a twin here.
      MuiPickersOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            fontSize: FIELD_LABEL_SIZE,
            // Same border as MuiOutlinedInput below.
            borderColor: 'var(--mui-palette-borderControl)',
          },
        },
      },
      MuiPickersInputBase: {
        styleOverrides: {
          sectionsContainer: ({ theme: t, ownerState }) => ({
            // Border-box by default; content-box lets FIELD_CONTENT_HEIGHT drive both slots.
            boxSizing: 'content-box',
            height: FIELD_CONTENT_HEIGHT,
            // Center the digit sections, like every other field.
            alignItems: 'center',
            // An empty date's "MM/DD/YYYY" is its placeholder; MUI only dims the text color.
            ...(ownerState.isFieldValueEmpty && !ownerState.isFieldFocused && {
              color: (t.vars || t).palette.placeholder,
            }),
          }),
        },
      },
      // MuiFormLabel.root is the rule that wins for shrunk outlined labels. Read `vars`, or
      // this bakes in the light value.
      MuiFormLabel: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            color: (t.vars || t).palette.inputLabel,
          }),
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          outlined: {
            fontSize: FIELD_LABEL_SIZE,
          },
        },
      },
      MuiBadge: {
        styleOverrides: {
          badge: ({ theme: t }) => ({
            fontSize: '0.875rem',
            fontWeight: 900,
            color: (t.vars || t).palette.status.primaryChipInk,
            backgroundColor: (t.vars || t).palette.status.primaryChip,
            border: `solid 1px ${(t.vars || t).palette.borderColor}`,
          }),
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: ({ theme: t }) => ({
            backgroundColor: (t.vars || t).palette.tableHeader,
            borderBottom: `1px solid ${(t.vars || t).palette.borderColor}`,
            boxShadow: (t.vars || t).palette.elevation.rim,
          }),
          stickyHeader: ({ theme: t }) => ({
            backgroundColor: (t.vars || t).palette.tableHeader,
          }),
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: {
            // Drop MUI's dark-elevation white overlay; it greys the family tint.
            backgroundImage: 'none',
          },
          // `elevation0` must stay flat; nested Papers and the side nav drawer opt out with it.
          elevation0: {
            boxShadow: 'none',
          },
        },
        variants: [
          {
            props: ({ elevation }) => elevation >= 1 && elevation <= 3,
            style: ({ theme: t }) => ({
              boxShadow: `${(t.vars || t).palette.elevation.raised}, ${(t.vars || t).palette.elevation.rim}`,
            }),
          },
          {
            props: ({ elevation }) => elevation >= 4 && elevation <= 8,
            style: ({ theme: t }) => ({
              boxShadow: `${(t.vars || t).palette.elevation.overlay}, ${(t.vars || t).palette.elevation.rim}`,
            }),
          },
          {
            props: ({ elevation }) => elevation >= 9,
            style: ({ theme: t }) => ({
              boxShadow: `${(t.vars || t).palette.elevation.modal}, ${(t.vars || t).palette.elevation.rim}`,
            }),
          },
        ],
      },
      MuiCard: {
        defaultProps: {
          // The shadow comes from the theme, not from MUI's elevation scale.
          elevation: 0,
        },
        styleOverrides: {
          root: ({ theme: t }) => ({
            backgroundColor: (t.vars || t).palette.cardBackground,
            border: `1px solid ${(t.vars || t).palette.border}`,
            boxShadow: (t.vars || t).palette.elevation.raised,
            backgroundImage: 'none',
          }),
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme: t }) => ({
            boxShadow: (t.vars || t).palette.elevation.modal,
            border: `1px solid ${(t.vars || t).palette.border}`,
            backgroundImage: 'none',
          }),
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: ({ theme: t }) => ({
            boxShadow: (t.vars || t).palette.elevation.overlay,
            border: `1px solid ${(t.vars || t).palette.border}`,
            backgroundImage: 'none',
          }),
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: ({ theme: t }) => ({
            boxShadow: (t.vars || t).palette.elevation.overlay,
            border: `1px solid ${(t.vars || t).palette.border}`,
            backgroundImage: 'none',
          }),
        },
      },
      // MUI X derives its own --DataGrid-t-* colours; point them at StickyHeadTable's tokens.
      MuiDataGrid: {
        defaultProps: {
          // The grid virtualizes rows, so CSS nth-child would flip parity while scrolling.
          getRowClassName: ({ indexRelativeToCurrentPage }) => (
            indexRelativeToCurrentPage % 2 ? 'odd' : ''
          ),
        },
        styleOverrides: {
          root: ({ theme: t }) => {
            const p = (t.vars || t).palette;
            const overlay = `linear-gradient(${p.rowHoverOverlay}, ${p.rowHoverOverlay})`;
            return {
              // `&&`: MUI X declares these on its own MuiDataGridVariables class on the same
              // element, inserted later, so a single class loses.
              '&&': {
                '--DataGrid-t-header-background-base': p.tableHeader,
                '--DataGrid-t-color-border-base': p.borderColor,
                // Row lines only; the frame and column separators keep the base color.
                '--DataGrid-rowBorderColor': p.TableCell.border,
              },
              '& .MuiDataGrid-columnHeader': { backgroundColor: p.tableHeader },
              '& .MuiDataGrid-row.odd': { backgroundColor: p.stripe },
              // Composite the hover over the row's base, never replace it.
              // `--link` and the underline: same reason as StickyHeadTable's row hover.
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'inherit', backgroundImage: overlay, '--link': 'var(--link-hover)',
              },
              '& .MuiDataGrid-row a:hover': { textDecoration: 'underline' },
              '& .MuiDataGrid-row.odd:hover': { backgroundColor: p.stripe },
            };
          },
        },
      },
      // MUI's delete × doesn't read the palette and fails 3:1 on the card.
      MuiChip: {
        styleOverrides: {
          deleteIcon: ({ theme: t }) => ({
            color: (t.vars || t).palette.iconMuted,
            '&:hover': { color: (t.vars || t).palette.text.primary },
          }),
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            // Sizes the notch; see FIELD_LABEL_SIZE.
            fontSize: FIELD_LABEL_SIZE,
            // Otherwise MUI's stock grey. Doesn't need 3:1: the well identifies the field.
            borderColor: 'var(--mui-palette-borderControl)',
          },
          // No hover brightening on read-only fields (`Mui-readOnly`, set by MUI): it
          // advertises editing that isn't possible.
          root: {
            '&.Mui-readOnly:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--mui-palette-borderControl)',
            },
          },
        },
      },
    },
    zIndex: {
      drawer: 1200,
      nav: 1201,
      swipeDrawer: 1201,
    },
  });
}

const theme = createAppTheme();

export default theme;
